import { state } from '@angular/animations';
import { Component, OnInit, ViewChild, Output, EventEmitter, OnDestroy, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { switchMap, debounceTime, finalize, tap, catchError } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';
import { RecordSuggestion } from 'src/app/models/record';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyInput as MatInput } from '@angular/material/legacy-input';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { SearchModel, SearchState } from 'src/app/models/search-model';
import { SearchCriteria } from 'src/app/state/user/searches/models/search';
import { Util } from 'src/app/util';
import { Location } from '@angular/common';

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SearchBarComponent implements OnInit, OnDestroy {
  @ViewChild('queryField', { static: false })
  queryRef: MatInput;

  @ViewChild(MatMenuTrigger, { static: false })
  menuRef: MatMenuTrigger;

  @ViewChild(MatAutocompleteTrigger, { static: false })
  autocomplete: MatAutocompleteTrigger;

  @Output()
  submitted: EventEmitter<boolean> = new EventEmitter();

  searchForm: UntypedFormGroup;
  suggestions: Observable<RecordSuggestion[]>;
  sidebarSub: Subscription;
  searchModelSub = new Subscription();
  searchModel = new SearchModel();
  selectedCriteria: SearchCriteria;
  arrowPressed: boolean;
  disableSuggestions: boolean;

  keepSearching = true;
  lastValidTerm: string;
  isSideBarOpen = false;
  isSuggestionRequestCancelled = true;
  isPreviousSuggestionRequestFinalized = true;

  constructor(
    broadcastService: BroadcastService,
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    private router: Router,
    private _location: Location
  ) {
    this.sidebarSub = broadcastService.sidebar$.subscribe((value) => {
      this.isSideBarOpen = value;
      if (value) {
        this.queryRef?.focus();
      } else {
        this.autocomplete?.closePanel();
        this.menuRef?.closeMenu();
      }
    });

    this.searchModelSub = broadcastService.searchModel$.subscribe((model) => (this.searchModel = model));
    this.selectedCriteria = this.searchModel.getSearchCriterias()[0];
  }

  ngOnInit() {
    this.searchForm = this.formBuilder.group({
      query: ['', Validators.maxLength(60)],
    });

    this.suggestions = this.searchForm.controls.query.valueChanges.pipe(
      debounceTime(300),
      switchMap((value) => {
        if (!this.isPreviousSuggestionRequestFinalized) {
          return;
        }

        if (value === this.lastValidTerm) {
          this.keepSearching = true;
        }

        if (value && value.length > 3 && this.keepSearching) {
          this.disableSuggestions = false;
          this.isPreviousSuggestionRequestFinalized = false;

          return this.apiService.getRecordSuggestions(value, this.getSuggestionCriteria()).pipe(
            tap((results) => {
              this.isSuggestionRequestCancelled = false;
              this.keepSearching = results.length > 0;

              if (this.keepSearching) {
                this.lastValidTerm = value;
              }
            }),
            catchError(() => {
              this.isSuggestionRequestCancelled = false;
              this.keepSearching = false;
              return [];
            }),
            finalize(() => {
              this.isPreviousSuggestionRequestFinalized = true;
              if (this.isSuggestionRequestCancelled) {
                this.keepSearching = false;
              }
              this.autocomplete.openPanel();
            })
          );
        }

        this.autocomplete.closePanel();
        this.disableSuggestions = true;
        return [];
      })
    );
  }

  ngOnDestroy() {
    this.sidebarSub.unsubscribe();
    this.searchModelSub.unsubscribe();
  }

  onEnter(event: KeyboardEvent) {
    event.preventDefault();

    if (!this.arrowPressed) {
      this.submit();
    }
  }

  onArrows() {
    if (this.autocomplete.panelOpen) {
      this.arrowPressed = true;
    }
  }

  handleEvent(event: MouseEvent) {
    event.stopPropagation();
  }

  getSuggestionCriteria(): string {
    switch (this.selectedCriteria.field) {
      default:
      case Util.ALLFIELDS:
        return null;
      case Util.AUTHOR_FIELD:
        return 'fndPartAuthor';
      case Util.TITLE_FIELD:
        return 'fndPartTitle';
      case Util.PUBLISHER_FIELD:
        return 'fndPartPublisher';
      case Util.SUBJECT_FIELD:
        return 'fndPartSubject';
    }
  }

  submit() {
    this.submitted.emit(true);
    const term = this.searchForm.value.query;
    this.searchModel.removeFixedQuery();

    if (!term) {
      this.searchModel.reset();
    } else {
      this.searchModel.collectionId = undefined;
      this.searchModel.offset = 0;
      this.searchModel.addTerm({
        field: this.selectedCriteria.field,
        term: term,
        icon: this.selectedCriteria.icon,
      });
    }

    const state: SearchState = { ...new SearchState(), searchModel: this.searchModel };
    this.router
      .navigate(['/results'], {
        queryParams: this.searchModel.toRequest(state),
      })
      .then(() => {
        document.querySelector<HTMLDivElement>('app-results-component .accessibility-focus-element')?.focus();
        window.scrollTo({ top: 0 });
      });
    this.autocomplete.closePanel();
    this.searchForm.controls.query.setValue(null);
  }

  goTo(event: MatAutocompleteSelectedEvent) {
    const selected: RecordSuggestion = event.option.value;
    const criterias = this.searchModel.getSearchCriterias();

    this.searchForm.setValue({ query: '' });

    let filter: string;
    let criteria: SearchCriteria;

    switch (selected.filter) {
      case 'fndPartTitle_ss':
        if (selected.format === 'LIST') {
          const prevNav = this._location.path();
          // TODO --> Return wishList ID in suggestions endpoint
          this.apiService.getRecord(selected.recordId).subscribe((record) => {
            if (record && record.wishListInfo) {
              const slug = Util.slugify(record.title) + '-' + record.wishListInfo.id;
              const state = {
                prevNav,
              } as SearchState;
              return this.router.navigate(['/list', slug], { state });
            } else {
              const slug = Util.slugify(selected.label) + '-' + selected.recordId;
              return this.router.navigate(['/info', slug]);
            }
          });
        } else {
          const slug = Util.slugify(selected.label) + '-' + selected.recordId;
          return this.router.navigate(['/info', slug]);
        }

      case 'fndPartAuthor_ss':
        filter = Util.AUTHOR_FIELD;
        break;
      case 'fndPartPublisher_ss':
        filter = Util.PUBLISHER_FIELD;
        break;
      case 'fndPartSubject_ss':
        filter = Util.SUBJECT_FIELD;
        break;
    }

    criteria = criterias.find((c) => c.field === filter);

    this.searchModel.addTerm({
      term: `"${selected.label}"`,
      field: this.getFilterName(selected.filter),
      icon: criteria.icon,
    });

    this.submitted.emit(true);
    this.router.navigate(['/results'], {
      queryParams: this.searchModel.toRequest(),
    });
  }

  onSelectCriteria(criteria: SearchCriteria) {
    this.selectedCriteria = criteria;
  }

  getFilterIcon(filter: string) {
    switch (filter) {
      case 'fndPartTitle_ss':
        return 'title';
      case 'fndPartAuthor_ss':
        return 'user';
      case 'fndPartPublisher_ss':
        return 'publisher';
      case 'fndPartSubject_ss':
        return 'subject';
      default:
        return 'help'; // fallback
    }
  }

  // TODO: generalize in SearchModel!!!!
  getFilterName(filter: string) {
    switch (filter) {
      case 'fndPartTitle_ss':
        return Util.TITLE_FIELD;
      case 'fndPartAuthor_ss':
        return Util.AUTHOR_FIELD;
      case 'fndPartPublisher_ss':
        return Util.PUBLISHER_FIELD;
      case 'fndPartSubject_ss':
        return Util.SUBJECT_FIELD;
    }
  }
}
